import { useState, useEffect } from "react";
import {
  Form,
  Input,
  Button,
  Upload,
  Modal,
  message,
  Space,
  Select,
} from "antd";
import { PlusOutlined, MinusCircleOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { useHttp } from "../../../utils/DataHendler";

const getBase64 = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result);
    reader.onerror = (error) => reject(error);
  });

export const JobForm = () => {
  const [form] = Form.useForm();
  const [fileList, setFileList] = useState([]);
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewVisible, setPreviewVisible] = useState(false);
  const [previewImage, setPreviewImage] = useState("");
  const [previewTitle, setPreviewTitle] = useState("");
  const { uploadFile, postData, getData } = useHttp();
  const navigate = useNavigate();
  const [loadingSubmit, setLoadingSubmit] = useState(false);

  // State untuk dropdown
  const [departments, setDepartments] = useState([]);
  const [jobCategories, setJobCategories] = useState([]);
  const [loadingDepartment, setLoadingDepartment] = useState(false);
  const [loadingCategory, setLoadingCategory] = useState(false);

  // Fetch department
  const getDepartment = async () => {
    setLoadingDepartment(true);
    try {
      const response = await getData("/department", {}, true);
      setDepartments(response?.data?.data || []);
    } catch (error) {
      console.error(error);
    } finally {
      setLoadingDepartment(false);
    }
  };

  // Fetch job category
  const getJobCategory = async () => {
    setLoadingCategory(true);
    try {
      const response = await getData("/job-category", {}, true);
      setJobCategories(response?.data?.data || []);
    } catch (error) {
      console.error(error);
    } finally {
      setLoadingCategory(false);
    }
  };

  useEffect(() => {
    getDepartment();
    getJobCategory();
  }, []);

  const handlePreview = async (file) => {
    if (!file.url && !file.preview) {
      file.preview = await getBase64(file.originFileObj);
    }
    setPreviewImage(file.url || file.preview);
    setPreviewVisible(true);
    setPreviewTitle(
      file.name || file.url?.substring(file.url.lastIndexOf("/") + 1)
    );
  };

  const handleChange = ({ fileList: newFileList }) => {
    setFileList(newFileList);
    if (newFileList.length > 0) {
      setSelectedFile(newFileList[0].originFileObj);
    } else {
      setSelectedFile(null);
    }
  };

  const beforeUpload = (file) => {
    // const isJpgOrPng =
    //   file.type === "image/jpeg" ||
    //   file.type === "image/jpg" ||
    //   file.type === "image/svg" ||
    //   file.type === "image/png";
    // if (!isJpgOrPng) {
    //   message.error("Hanya file JPG/PNG yang diperbolehkan!");
    //   return Upload.LIST_IGNORE;
    // }

    const isLt5M = file.size / 1024 / 1024 < 5;
    if (!isLt5M) {
      message.error("Ukuran gambar tidak boleh lebih dari 5MB!");
      return Upload.LIST_IGNORE;
    }

    return true;
  };

  const handleCancel = () => setPreviewVisible(false);

  const onFinish = async (values) => {
    setLoadingSubmit(true);
    if (!selectedFile) {
      message.error("Silakan upload gambar terlebih dahulu.");
      setLoadingSubmit(false);
      return;
    }

    try {
      const result = await uploadFile("/upload/file", selectedFile);
      const imageUrl = result.data.preview_url || result?.data?.url;

      if (!imageUrl) {
        throw new Error("Upload tidak mengembalikan URL gambar.");
      }

      await postData(
        `/job`,
        {
          title: values.title,
          description: values.description,
          department_id: parseInt(values.department_id),
          job_category_id: parseInt(values.job_category_id),
          image_url: imageUrl,
          email: values.email,
          requirement: values.requirement || [],
        },
        {},
        true
      );

      message.success("Lowongan berhasil ditambahkan.");
      form.resetFields();
      setFileList([]);
      setSelectedFile(null);
      navigate("/user");
    } catch (err) {
      console.error("Upload failed", err);
      message.error("Gagal menyimpan data job.");
    } finally {
      setLoadingSubmit(false);
    }
  };

  return (
    <Form
      form={form}
      layout="vertical"
      onFinish={onFinish}
      style={{ maxWidth: 600, margin: "0 auto" }}
    >
      <Form.Item
        name="title"
        label="Judul Pekerjaan"
        rules={[{ required: true, message: "Judul pekerjaan wajib diisi" }]}
      >
        <Input placeholder="Masukkan judul pekerjaan" />
      </Form.Item>

      <Form.Item
        name="email"
        label="Email"
        rules={[{ required: true, message: "Email pekerjaan wajib diisi" }]}
      >
        <Input placeholder="Masukkan Email" />
      </Form.Item>

      <Form.Item
        name="description"
        label="Deskripsi"
        rules={[{ required: true, message: "Deskripsi wajib diisi" }]}
      >
        <Input.TextArea rows={4} placeholder="Masukkan deskripsi pekerjaan" />
      </Form.Item>

      <Form.Item
        name="department_id"
        label="Department"
        rules={[{ required: true, message: "Pilih department" }]}
      >
        <Select
          loading={loadingDepartment}
          disabled={loadingDepartment}
          placeholder="Pilih Department"
          options={departments.map((el) => ({
            value: el.id,
            label: el.name,
          }))}
        />
      </Form.Item>

      <Form.Item
        name="job_category_id"
        label="Job Category"
        rules={[{ required: true, message: "Pilih job category" }]}
      >
        <Select
          loading={loadingCategory}
          disabled={loadingCategory}
          placeholder="Pilih Job Category"
          options={jobCategories.map((el) => ({
            value: el.id,
            label: el.name,
          }))}
        />
      </Form.Item>

      <Form.List
        name="requirement"
        rules={[
          {
            validator: async (_, requirements) => {
              if (!requirements || requirements.length < 1) {
                return Promise.reject(
                  new Error("Minimal 1 persyaratan diperlukan")
                );
              }
            },
          },
        ]}
      >
        {(fields, { add, remove }) => (
          <>
            <Form.Item label="Persyaratan" required style={{ marginBottom: 0 }}>
              {fields.map((field) => (
                <Space
                  key={field.key}
                  style={{ display: "flex", marginBottom: 8 }}
                  align="baseline"
                >
                  <Form.Item
                    {...field}
                    style={{ flex: 1 }}
                    rules={[
                      {
                        required: true,
                        message: "Persyaratan tidak boleh kosong",
                      },
                    ]}
                  >
                    <Input placeholder="Masukkan persyaratan" />
                  </Form.Item>
                  <MinusCircleOutlined onClick={() => remove(field.name)} />
                </Space>
              ))}
              <Button
                type="dashed"
                onClick={() => add()}
                block
                icon={<PlusOutlined />}
              >
                Tambah Persyaratan
              </Button>
            </Form.Item>
          </>
        )}
      </Form.List>

      <br />
      <Form.Item label="Upload Gambar Produk" required>
        <Upload
          listType="picture-card"
          fileList={fileList}
          onPreview={handlePreview}
          onChange={handleChange}
          beforeUpload={beforeUpload}
          maxCount={1}
        >
          {fileList.length >= 1 ? null : (
            <div>
              <PlusOutlined />
              <div style={{ marginTop: 8 }}>Upload</div>
            </div>
          )}
        </Upload>

        <Modal
          open={previewVisible}
          title={previewTitle}
          footer={null}
          onCancel={handleCancel}
        >
          <img alt="preview" style={{ width: "100%" }} src={previewImage} />
        </Modal>
      </Form.Item>

      <Form.Item>
        <Space>
          <Button loading={loadingSubmit} type="primary" htmlType="submit">
            Simpan
          </Button>
          <Button
            onClick={() => {
              form.resetFields();
              setFileList([]);
              setSelectedFile(null);
            }}
          >
            Reset
          </Button>
        </Space>
      </Form.Item>
    </Form>
  );
};
